import { useState } from "react";
import { Api, ApiError, type SiteCheck, type SiteReport } from "../api.js";
import { useI18n, type Translate } from "../i18n.js";

const MARK: Record<SiteCheck["status"], string> = { ok: "✓", fail: "✗", warn: "!", info: "i" };

/** The sentence for one finding; the server sends codes and a few facts, the words are here (and translated). */
export function describe(t: Translate, c: SiteCheck): string {
  const key = `check.${c.id}.${c.code}`;
  return t(key as never, c.params ?? {});
}

/**
 * "Check my website": the server reads a page of the owner's site as text and reports in plain words. Nothing from the
 * page is run. `onAllow` adds the site to the allowed domains in the form (published with the next Publish).
 */
export function CheckSite({ api, agentId, onAllow }: { api: Api; agentId: string; onAllow: (entries: string[]) => void }) {
  const { t } = useI18n();
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<SiteReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState(false);

  const run = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    setError(null);
    setReport(null);
    setAdded(false);
    try {
      setReport(await api.checkSite(agentId, url));
    } catch (err) {
      setError(err instanceof ApiError && err.status === 429 ? t("check.p.rate_limited") : err instanceof ApiError ? err.message : t("common.network"));
    } finally {
      setBusy(false);
    }
  };

  const problem = report?.problem;
  const builder = report?.checks.find((c) => c.id === "builder");
  const rows = report?.checks.filter((c) => c.id !== "builder") ?? [];
  return (
    <>
      <p className="help">{t("check.help")}</p>
      <form className="row" onSubmit={run}>
        <div className="field grow">
          <label htmlFor="check-url">{t("check.label")}</label>
          <input id="check-url" type="text" inputMode="url" autoComplete="off" placeholder="www.mysite.com" value={url} onChange={(e) => setUrl(e.target.value)} />
        </div>
        <button className="btn secondary" type="submit" disabled={busy || !url.trim()}>{busy ? t("check.busy") : t("check.button")}</button>
      </form>
      <div role="status" aria-live="polite">
        {error && <p className="field-error">{error}</p>}
        {problem && <p className="field-error">{t(`check.p.${problem}` as never, { status: String(report?.status ?? "") })}</p>}
        {report && !problem && (
          <>
            <ul className="checks">
              {rows.map((c) => (
                <li key={c.id} className={`check ${c.status}`}>
                  <span className="mark" aria-hidden="true">{MARK[c.status]}</span>
                  <span className="sr-only">{t(`check.status.${c.status}` as never)}: </span>
                  <span>{describe(t, c)}</span>
                  {c.id === "allowed" && c.code === "not_allowed" && report.entries.length > 0 && (
                    <>
                      {" "}
                      <button type="button" className="btn secondary" disabled={added} onClick={() => { onAllow(report.entries); setAdded(true); }}>
                        {added ? t("check.allowed.added") : t("check.allowed.add", { entries: report.entries.join(", ") })}
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
            {builder && <p className="hint">{describe(t, builder)}</p>}
            <p><strong>{report.ok ? t("check.good") : t("check.fix")}</strong></p>
          </>
        )}
      </div>
    </>
  );
}
